import { get, apiClient } from './client';
import type {
  AdminTransactionListResponse,
  AdminTransactionDetail,
  TransactionDetailWithTimeline,
  ListAdminTransactionsParams,
} from '@/types/admin-transaction';
import type { AdminTraceResponse } from '@/types/admin-trace';

export async function listAdminTransactions(
  params?: ListAdminTransactionsParams
): Promise<AdminTransactionListResponse> {
  const queryParams: Record<string, string> = {};
  if (params?.merchantId) queryParams.merchantId = params.merchantId;
  // Spec 409: multi-value `+/-` lists + substring (singular param names per the BFF contract).
  if (params?.service) queryParams.service = params.service;
  if (params?.status) queryParams.status = params.status;
  if (params?.operator) queryParams.operator = params.operator;
  if (params?.txId) queryParams.txId = params.txId;
  // Issue #300: flowType/gatewayMode wurden bisher verschluckt — die
  // Filter-Dropdowns setzten den State, der Request trug ihn nie. Param-Namen
  // exakt flowType/gatewayMode (so liest sie das BFF: rawParams.flowType/.gatewayMode).
  if (params?.flowType) queryParams.flowType = params.flowType;
  if (params?.gatewayMode) queryParams.gatewayMode = params.gatewayMode;
  // Spec 475: v2.17 server-side searches (exact IQ id / customerReference substring).
  if (params?.internetqTransactionId) queryParams.internetqTransactionId = params.internetqTransactionId;
  if (params?.customerReference) queryParams.customerReference = params.customerReference;
  // #482: v2.18 exact reference search (GSI-backed).
  if (params?.customerReferenceExact) queryParams.customerReferenceExact = params.customerReferenceExact;
  if (params?.limit) queryParams.limit = String(params.limit);
  if (params?.cursor) queryParams.cursor = params.cursor;
  // Spec 475: aktive Suche → KEIN HTTP-Client-Retry (Muster getTransactionTrace).
  // Eine trefferlose customerReference-Suche kann facade-seitig bis ans
  // 29-s-API-GW-Timeout laufen — der Standard-Retry (3x bei 502/503/504)
  // wuerde diesen Scan vervielfachen statt einen Fehler zu heilen.
  // customerReferenceExact zaehlt mit: faellt das Facade-Feature-Flag auf den
  // Scan-Fallback zurueck, gilt dieselbe Timeout-Oekonomie.
  const searchActive = !!(
    params?.txId ||
    params?.internetqTransactionId ||
    params?.customerReference ||
    params?.customerReferenceExact
  );
  if (searchActive) {
    return apiClient<AdminTransactionListResponse>('/admin/transactions', {
      method: 'GET',
      params: queryParams,
      noRetry: true,
    });
  }
  return get<AdminTransactionListResponse>('/admin/transactions', queryParams);
}

/**
 * Get a single transaction by ID.
 * Spec 0027: Optional merchantId hint for platform_admin routing via Facade.
 * Spec 479: `noRetry` fuer Kaskaden-Stufe 2 — ein 504-Retry-Dreifach wuerde
 * die Stop-on-Error-Politik der Suche unterlaufen (Muster getTransactionTrace).
 * Default-Verhalten (Detail-Seite) bleibt unveraendert retry-behaftet.
 */
export async function getAdminTransaction(
  transactionId: string,
  merchantId?: string,
  opts?: { noRetry?: boolean },
): Promise<AdminTransactionDetail> {
  const params: Record<string, string> = {};
  if (merchantId) params.merchantId = merchantId;
  const endpoint = `/admin/transactions/${encodeURIComponent(transactionId)}`;
  if (opts?.noRetry) {
    return apiClient<AdminTransactionDetail>(endpoint, {
      method: 'GET',
      params,
      noRetry: true,
    });
  }
  return get<AdminTransactionDetail>(endpoint, params);
}

/**
 * Get transaction detail with timeline.
 * Spec 141: BFF proxy to Facade GET /api/v1/admin/transactions/{id}/detail
 */
export async function getTransactionDetail(
  transactionId: string,
): Promise<TransactionDetailWithTimeline> {
  return get<TransactionDetailWithTimeline>(
    `/admin/transactions/${encodeURIComponent(transactionId)}/detail`,
  );
}

/**
 * Get transaction trace (Spec 143).
 * BFF proxy to Facade GET /api/v1/admin/transactions/{id}/trace.
 * Mit includeCloudWatch wird ?include=cloudwatch gesendet — die Facade
 * korreliert dann CloudWatch-Logs (~2-15s Antwortzeit, Platform-Rollen only).
 */
export async function getTransactionTrace(
  transactionId: string,
  opts?: { includeCloudWatch?: boolean },
): Promise<AdminTraceResponse> {
  const endpoint = `/admin/transactions/${encodeURIComponent(transactionId)}/trace`;
  if (opts?.includeCloudWatch) {
    // Teure CW-Query (2-15s): KEIN HTTP-Client-Retry. Das retry:false im
    // React-Query-Hook stoppt nur Query-Retries — der apiClient wuerde bei
    // 502/503/504 sonst zusaetzlich bis zu 3x wiederholen (4x 15s worst case).
    return apiClient<AdminTraceResponse>(endpoint, {
      method: 'GET',
      params: { include: 'cloudwatch' },
      noRetry: true,
    });
  }
  // Basis-Trace (<500ms) darf normal retrien
  return get<AdminTraceResponse>(endpoint);
}
